import { useState } from 'react';
import {
    Button,
    Divider,
    Group,
    Menu,
    NumberInput,
    Select,
    Stack,
    Text
} from '@mantine/core';

import { useGenerateMessage } from '../hooks/useGenerateMessage';
import { isTestDataEnabled } from '../isTestDataEnabled';

/**
 * FNP-1521: the visible, explicit action that generates test messages.
 *
 * This is the product capability the owner's standing guideline calls for
 * (CLAUDE.md, "TEST DATA IS A PRODUCT FEATURE, NEVER A SEED SCRIPT"): an
 * operator opens the messages list, sees a control, chooses how many rows and
 * what shape, and clicks. Nothing is seeded at deploy time and no script writes
 * rows behind anyone's back, so every fixture row in the table has a visible
 * author and a moment it was created.
 *
 * ⚠️ RETURNS NULL IN PROD, and that is a convenience, not the control — see
 * `isTestDataEnabled`. The mutation itself has no resolver in the prod stack.
 *
 * The options are DIALS rather than a fixed menu of counts: how many rows and
 * what shape they take are settings the operator chooses per run. An untouched
 * form generates a realistic MIX — varied statuses, some attachments — because
 * a mailbox where every row is identical exercises neither the list nor the
 * detail view.
 */
export const GenerateMessageButton = () => {
    const { generate, generating } = useGenerateMessage();

    const [count, setCount] = useState<number>(5);
    const [status, setStatus] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<string | null>(null);
    const [opened, setOpened] = useState(false);

    // Absent in prod, not merely disabled: a greyed-out control invites someone to
    // ask why it is greyed out, and the answer is that the capability does not
    // exist there at all.
    if (!isTestDataEnabled()) {
        return null;
    }

    const onGenerate = async () => {
        await generate({
            count,
            // null means "vary it" — the API treats an omitted dial as vary, so
            // send nothing rather than a default that would flatten the mix.
            ...(status ? { status } : {}),
            ...(attachments === null ? {} : { withAttachments: attachments === 'with' })
        });
        setOpened(false);
    };

    return (
        <Menu
            withinPortal
            opened={opened}
            onChange={setOpened}
            closeOnItemClick={false}
            position='bottom-end'
        >
            <Menu.Target>
                <Button variant='default' loading={generating}>
                    Generate Test Data
                </Button>
            </Menu.Target>

            <Menu.Dropdown>
                <Stack gap='xs' p='sm' w={260}>
                    <Text size='sm' fw={600}>
                        Generate test messages
                    </Text>
                    <Text size='xs' c='dimmed'>
                        Written into your own mailbox and tagged as generated, so they
                        can be told apart from real mail.
                    </Text>

                    <Divider my='xs' />

                    <NumberInput
                        label='How many'
                        min={1}
                        max={50}
                        value={count}
                        onChange={(value) => setCount(Number(value) || 1)}
                    />

                    <Select
                        label='Status'
                        placeholder='Varied'
                        clearable
                        value={status}
                        onChange={setStatus}
                        data={[
                            { value: 'UNREAD', label: 'Unread' },
                            { value: 'READ', label: 'Read' },
                            { value: 'SENT', label: 'Sent' }
                        ]}
                    />

                    <Select
                        label='Attachments'
                        placeholder='Varied'
                        clearable
                        value={attachments}
                        onChange={setAttachments}
                        data={[
                            { value: 'with', label: 'Every message' },
                            { value: 'without', label: 'None' }
                        ]}
                    />

                    <Group justify='flex-end' mt='xs'>
                        <Button size='xs' loading={generating} onClick={onGenerate}>
                            Generate
                        </Button>
                    </Group>
                </Stack>
            </Menu.Dropdown>
        </Menu>
    );
};
